import { useState, useRef, useEffect } from 'react'
import { PageHeader } from '@/components/common/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Save, Upload, Trash2, Eye, EyeOff, ChevronDown, ChevronUp } from 'lucide-react'
import { useAuthStore } from '@/store/auth-store'
import { userProfileService } from '@/services/user-profile-service'

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp']

export function UserProfilePage() {
  const { user, setUser } = useAuthStore()

  // Profile fields
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Password fields
  const [showPassword, setShowPassword] = useState(false)
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showOldPw, setShowOldPw] = useState(false)
  const [showNewPw, setShowNewPw] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Load profile on mount
  useEffect(() => {
    const load = async () => {
      try {
        const profile = await userProfileService.getMe()
        setFirstName(profile.firstName || '')
        setLastName(profile.lastName || '')
        setAvatarPreview(profile.avatarUrl || null)
        setUser(profile)
      } catch {
        // Fall back to store data
        if (user) {
          setFirstName(user.firstName || '')
          setLastName(user.lastName || '')
          setAvatarPreview(user.avatarUrl || null)
        }
      }
    }
    load()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveProfile = async () => {
    setIsSaving(true)
    setSaveMessage(null)
    try {
      const updated = await userProfileService.updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        avatarUrl: avatarPreview,
      })
      setUser({ ...user!, ...updated })
      setSaveMessage({ type: 'success', text: 'Profile saved' })
      setTimeout(() => setSaveMessage(null), 3000)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save profile'
      setSaveMessage({ type: 'error', text: msg })
    } finally {
      setIsSaving(false)
    }
  }

  const compressImage = (file: File, maxSize: number = 256): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      const url = URL.createObjectURL(file)
      img.onload = () => {
        URL.revokeObjectURL(url)
        const canvas = document.createElement('canvas')
        // Scale down to maxSize x maxSize (profile photos don't need to be large)
        let w = img.width
        let h = img.height
        if (w > maxSize || h > maxSize) {
          if (w > h) { h = Math.round(h * maxSize / w); w = maxSize }
          else { w = Math.round(w * maxSize / h); h = maxSize }
        }
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        if (!ctx) { reject(new Error('Canvas not supported')); return }
        ctx.drawImage(img, 0, 0, w, h)
        // Always output as JPEG for consistent small size
        resolve(canvas.toDataURL('image/jpeg', 0.85))
      }
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')) }
      img.src = url
    })
  }

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setSaveMessage({ type: 'error', text: 'Please upload a PNG, JPEG, SVG, or WebP image' })
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      setSaveMessage({ type: 'error', text: 'Image must be under 5MB' })
      return
    }

    try {
      const compressed = await compressImage(file)
      setAvatarPreview(compressed)
    } catch {
      setSaveMessage({ type: 'error', text: 'Failed to process image' })
    }
    // Reset input so same file can be re-selected
    e.target.value = ''
  }

  const handleRemoveAvatar = () => {
    setAvatarPreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleChangePassword = async () => {
    setPasswordMessage(null)

    if (!oldPassword) {
      setPasswordMessage({ type: 'error', text: 'Enter your current password' })
      return
    }
    if (newPassword.length < 8) {
      setPasswordMessage({ type: 'error', text: 'New password must be at least 8 characters' })
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Passwords do not match' })
      return
    }

    setIsChangingPassword(true)
    try {
      await userProfileService.changePassword(oldPassword, newPassword, confirmPassword)
      setPasswordMessage({ type: 'success', text: 'Password updated successfully' })
      setOldPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => setPasswordMessage(null), 3000)
    } catch (err: unknown) {
      const errorData = (err as { response?: { data?: { message?: string } } })?.response?.data
      const msg = errorData?.message || 'Failed to change password'
      setPasswordMessage({ type: 'error', text: msg })
    } finally {
      setIsChangingPassword(false)
    }
  }

  const initials = `${firstName || ''} ${lastName || ''}`
    .trim()
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '??'

  return (
    <div className="up-page">
      <PageHeader title="My Profile" description="Manage your personal information and password" />

      <div className="up-container">
        {/* Avatar Section */}
        <div className="up-section">
          <h3 className="up-section-title">Profile Photo</h3>
          <div className="up-avatar-area">
            <div className="up-avatar-preview">
              {avatarPreview ? (
                <img src={avatarPreview} alt="Avatar" className="up-avatar-img" />
              ) : (
                <span className="up-avatar-initials">{initials}</span>
              )}
            </div>
            <div className="up-avatar-actions">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="up-avatar-input"
                onChange={handleAvatarUpload}
              />
              <div className="up-avatar-btn-row">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Upload Photo
                </Button>
                {avatarPreview && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveAvatar}
                    className="text-red-500 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Remove
                  </Button>
                )}
              </div>
              <p className="up-avatar-hint">PNG, JPEG, SVG or WebP. Max 5MB.</p>
            </div>
          </div>
        </div>

        {/* Profile Info Section */}
        <div className="up-section">
          <h3 className="up-section-title">Personal Information</h3>
          <div className="up-form-row">
            <div className="up-form-group">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
              />
            </div>
            <div className="up-form-group">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
              />
            </div>
          </div>
          <div className="up-form-group">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              value={user?.email || ''}
              disabled
              className="up-input-disabled"
            />
            <p className="up-field-hint">Your email is your username and cannot be changed here.</p>
          </div>

          <div className="up-save-row">
            <Button onClick={handleSaveProfile} disabled={isSaving}>
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
            {saveMessage && (
              <span className={saveMessage.type === 'success' ? 'up-msg-success' : 'up-msg-error'}>
                {saveMessage.text}
              </span>
            )}
          </div>
        </div>

        {/* Password Section */}
        <div className="up-section">
          <button
            className="up-section-toggle"
            onClick={() => setShowPassword(!showPassword)}
            type="button"
          >
            <h3 className="up-section-title" style={{ marginBottom: 0 }}>Change Password</h3>
            {showPassword ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>

          {showPassword && (
            <div className="up-password-form">
              <div className="up-form-group">
                <Label htmlFor="oldPassword">Current Password</Label>
                <div className="up-pw-input-wrap">
                  <Input
                    id="oldPassword"
                    type={showOldPw ? 'text' : 'password'}
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    className="up-pw-toggle"
                    onClick={() => setShowOldPw(!showOldPw)}
                  >
                    {showOldPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="up-form-group">
                <Label htmlFor="newPassword">New Password</Label>
                <div className="up-pw-input-wrap">
                  <Input
                    id="newPassword"
                    type={showNewPw ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 8 characters"
                  />
                  <button
                    type="button"
                    className="up-pw-toggle"
                    onClick={() => setShowNewPw(!showNewPw)}
                  >
                    {showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="up-form-group">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                />
              </div>

              <div className="up-save-row">
                <Button onClick={handleChangePassword} disabled={isChangingPassword}>
                  {isChangingPassword ? 'Updating...' : 'Update Password'}
                </Button>
                {passwordMessage && (
                  <span className={passwordMessage.type === 'success' ? 'up-msg-success' : 'up-msg-error'}>
                    {passwordMessage.text}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
